import type {
  GetTourConfigAndAvailabilityWebApiResponseDto,
  GetToursForListingWebApiResponseDto,
  PerformTourActionWebApiResponseDto,
  RemoveAvailabilityWebApiRequestDto,
  SetAvailabilityLiteDto,
  SetAvailabilityWebApiRequestDto,
  TourAvailabilityWebApiDto,
  UpdateAvailabilityWebApiRequestDto,
} from '@zg-rentals/ts-z-calendar-client';
import { RecurringFrequency, TourStatusForZcheduler, TourType } from '@zg-rentals/ts-z-calendar-client';
import { endOfDay, isBefore, transpose } from 'date-fns';
import { TZDate } from '@date-fns/tz';

import type {
  AvailabilityEvent,
  AvailabilityEventWeeklyRecurringDaysMap,
  TourEvent,
  WeeklyRecurringDays,
} from '../constants/TourCalendar';
import { DefaultSelectedWeeklyRecurringDays, Z_CALENDAR_EVENT_TYPE } from '../constants/TourCalendar';
import { convertDateToLocalDate, convertDateToLocalDateTime, convertLocalDateTimeToDate } from './formatTime';
import type { AvailabilityDate } from '../types/TourAvailabilityCards';

export const convertAvailabilityEventWeeklyRecurringDaysMapToRequestArray = (
  recurringDaysMap: AvailabilityEventWeeklyRecurringDaysMap,
) => {
  return Object.keys(recurringDaysMap).filter((day) => recurringDaysMap[day as WeeklyRecurringDays]);
};

const createRecurringParamsForRequestDto = (availabilityEvent: AvailabilityEvent) => {
  const { isRecurringEvent, frequency, recurrenceEndDateMs, recurringDays } = availabilityEvent;

  const recurrenceEndDate =
    recurrenceEndDateMs === undefined ? undefined : convertDateToLocalDate(new Date(recurrenceEndDateMs));

  if (isRecurringEvent && frequency === RecurringFrequency.DAILY) {
    return {
      isRecurring: isRecurringEvent,
      frequency: frequency as RecurringFrequency,
      recurrenceEndDate,
    };
  }

  if (isRecurringEvent && frequency === RecurringFrequency.WEEKLY) {
    return {
      isRecurring: isRecurringEvent,
      frequency: frequency as RecurringFrequency,
      recurringDays: recurringDays
        ? convertAvailabilityEventWeeklyRecurringDaysMapToRequestArray(recurringDays)
        : undefined,
      recurrenceEndDate,
    };
  }

  return {
    isRecurring: false,
  };
};

type CreateSetAvailabilityPostDataFromEventArgs = {
  event: AvailabilityEvent;
  listingAlias: string;
};

export const createSetAvailabilityPostDataFromEvent = ({
  event,
  listingAlias,
}: CreateSetAvailabilityPostDataFromEventArgs): SetAvailabilityWebApiRequestDto => {
  // @ts-expect-error - startTimeMs and endTimeMs are deprecated
  return {
    listingAlias,
    startDateTime: convertDateToLocalDateTime(event.start),
    endDateTime: convertDateToLocalDateTime(event.end),
    tourType: TourType.IN_PERSON,
    ...createRecurringParamsForRequestDto(event),
  };
};

const createAvailabilityEventWeeklyRecurringDaysMapFromTourAvailabilityDto = (
  tourAvailabilityDto: TourAvailabilityWebApiDto,
): AvailabilityEventWeeklyRecurringDaysMap => {
  const availabilityEventWeeklyRecurringDaysMap = { ...DefaultSelectedWeeklyRecurringDays };

  if (tourAvailabilityDto.recurringDays) {
    tourAvailabilityDto.recurringDays.forEach((day) => {
      availabilityEventWeeklyRecurringDaysMap[day as WeeklyRecurringDays] = true;
    });
  }

  return availabilityEventWeeklyRecurringDaysMap;
};

const getListingLocalEndOfDayMs = (ms: number, timezone: string) =>
  endOfDay(transpose(new TZDate(ms, timezone), Date)).getTime();

export const createEventsFromAvailabilityResponse = (
  data: GetTourConfigAndAvailabilityWebApiResponseDto,
): Array<AvailabilityEvent> => {
  return data.availabilities.map((availability) => {
    const {
      tourAvailabilityId,
      startDateTime,
      endDateTime,
      isRecurringEvent,
      frequency,
      recurrenceEndDateMs,
      timezone,
    } = availability;

    const start = convertLocalDateTimeToDate(startDateTime);
    const end = convertLocalDateTimeToDate(endDateTime);
    const isPastEvent = isBefore(end, new TZDate(new Date(), timezone));
    return {
      id: tourAvailabilityId,
      title: 'Available',
      start,
      end,
      eventType: Z_CALENDAR_EVENT_TYPE.AVAILABILITY,
      isRecurringEvent,
      frequency: frequency,
      recurringDays: createAvailabilityEventWeeklyRecurringDaysMapFromTourAvailabilityDto(availability),
      recurrenceEndDateMs: recurrenceEndDateMs ? getListingLocalEndOfDayMs(recurrenceEndDateMs, timezone) : undefined,
      isPastEvent,
    };
  });
};

type CreateUpdateAvailabilityPostDataFromEventArgs = {
  event: AvailabilityEvent;
  listingAlias: string;
};

export const createUpdateAvailabilityPostDataFromEvent = ({
  event,
  listingAlias,
}: CreateUpdateAvailabilityPostDataFromEventArgs): UpdateAvailabilityWebApiRequestDto => {
  // @ts-expect-error - startTimeMs and endTimeMs are deprecated
  return {
    tourAvailabilityId: event.id,
    listingAlias,
    startDateTime: convertDateToLocalDateTime(event.start),
    endDateTime: convertDateToLocalDateTime(event.end),
    tourType: TourType.IN_PERSON,
    ...createRecurringParamsForRequestDto(event),
    updateOnlyThisEvent: false,
  };
};

type CreateRemoveAvailabilityPostDataFromEventArgs = {
  event: AvailabilityEvent;
  listingAlias: string;
  removeOnlyThisEvent: boolean;
};

export const createRemoveAvailabilityPostDataFromEvent = ({
  event,
  listingAlias,
  removeOnlyThisEvent,
}: CreateRemoveAvailabilityPostDataFromEventArgs): RemoveAvailabilityWebApiRequestDto => {
  return {
    tourAvailabilityId: event.id,
    listingAlias,
    occurrenceDate: removeOnlyThisEvent ? convertDateToLocalDate(event.start) : undefined,
  };
};

export const createEventsFromBookedTourResponse = (
  toursWebApiResDto: GetToursForListingWebApiResponseDto,
): Array<TourEvent> => {
  return toursWebApiResDto.tours.map((tour) => {
    const { startDateTime, endDateTime, zoneId, ...rest } = tour;
    const start = convertLocalDateTimeToDate(startDateTime);
    const end = convertLocalDateTimeToDate(endDateTime);
    const isPastEvent = isBefore(end, new TZDate(new Date(), zoneId));
    return {
      start,
      end,
      eventType: Z_CALENDAR_EVENT_TYPE.TOUR,
      zoneId,
      isPastEvent,
      ...rest,
    };
  });
};

const ACTIVE_TOUR_STATUSES: ReadonlyArray<TourStatusForZcheduler> = [
  TourStatusForZcheduler.REQUESTED,
  TourStatusForZcheduler.CONFIRMED,
];

/**
 * Drops CANCELLED tour tiles for any renter who already has an active (REQUESTED or CONFIRMED)
 * tour in the same set — the landlord re-booked them, so the stale cancelled tile is redundant.
 * Renters are correlated by conversationId; events without one are never grouped and always kept.
 */
export const hideCancelledToursWithActiveTour = <
  T extends { conversationId?: string; tourStatus: TourStatusForZcheduler },
>(
  tourEvents: Array<T>,
): Array<T> => {
  const conversationIdsWithActiveTour = new Set(
    tourEvents
      .filter((event) => event.conversationId && ACTIVE_TOUR_STATUSES.includes(event.tourStatus))
      .map((event) => event.conversationId),
  );

  return tourEvents.filter(
    (event) =>
      !(
        event.tourStatus === TourStatusForZcheduler.CANCELLED &&
        event.conversationId &&
        conversationIdsWithActiveTour.has(event.conversationId)
      ),
  );
};

export const createUpdatedTourEvents = (
  existingTourEvents: Array<TourEvent>,
  performTourActionWebApiRes: PerformTourActionWebApiResponseDto,
): Array<TourEvent> => {
  return existingTourEvents.map((tour) => {
    if (tour.id === performTourActionWebApiRes.tour.id) {
      const { startDateTime, endDateTime, zoneId: _zoneId, ...rest } = performTourActionWebApiRes.tour;
      const start = convertLocalDateTimeToDate(startDateTime);
      const end = convertLocalDateTimeToDate(endDateTime);
      return {
        ...tour,
        ...rest,
        start,
        end,
      };
    }

    return tour;
  });
};

const getDateWithTime = (date: Date, timeToCopyDate: Date) => {
  const dateClone = new Date(date.getTime());
  dateClone.setHours(
    timeToCopyDate.getHours(),
    timeToCopyDate.getMinutes(),
    timeToCopyDate.getSeconds(),
    timeToCopyDate.getMilliseconds(),
  );
  return dateClone;
};

export const convertAvailabilityDatesToSetAvailabilityLiteDto = (
  availabilityDates: Array<AvailabilityDate>,
): Array<SetAvailabilityLiteDto> => {
  return availabilityDates.reduce((acc: Array<SetAvailabilityLiteDto>, availabilityDate) => {
    const date = new Date(availabilityDate.date);
    const timeSlots = availabilityDate.timeSlots;
    timeSlots.forEach((timeSlot: { start?: Date; end?: Date }) => {
      if (timeSlot.start && timeSlot.end) {
        acc.push({
          endDateTime: convertDateToLocalDateTime(getDateWithTime(date, timeSlot.end)),
          startDateTime: convertDateToLocalDateTime(getDateWithTime(date, timeSlot.start)),
          tourType: TourType.IN_PERSON,
        });
      }
    });
    return acc;
  }, []);
};
